<template>
  <div>
    <div v-if="activeStop" class="pick__active-stop">
      <ShelfBadge :label="activeStop.shelfSpaceId" class="pick__active-stop__shelf-badge" />
      <div style="position: relative;">
        <div
          :style="
            `background-image: url(https://storage.googleapis.com/emma-static/products/${
              product(activeStop.productId).img
            }_1200.jpg)`
          "
          class="pick__active-stop__product-img"
          alt="..."
        />
        <span class="pick__active-stop__amount" v-if="activeStop.amount > 1">{{ activeStop.amount }}x</span>
        <button
          @click="scannedProductId = activeStop.productId"
          class="position-absolute btn btn-sm btn-outline-secondary"
          style="bottom: 0; right: 0; background: rgba(255,255,255,0.75)"
        >
          No EAN
        </button>
      </div>
    </div>
    <h3 class="mt-5 mb-3 d-flex justify-content-between">
      <span>
        Open Stops
        <span
          class="badge ml-1"
          :class="stats.open === 0 && stats.missing === 0 ? 'badge-success' : 'badge-secondary'"
          >{{ stats.open }}</span
        >
      </span>
      <button class="btn btn-sm btn-outline-secondary" @click="showCompletedStops = true">
        {{ stats.completed }} Completed Stops
      </button>
    </h3>
    <ul class="list-group" v-if="stats.open > 0">
      <li
        v-for="(stop, stopIndex) in pickPlan.open"
        :key="stopIndex"
        class="list-group-item d-flex justify-content-between align-items-center stock-listing__item"
        @click="activeStopIndex = stopIndex"
        :style="`background-color: ${stopIndex === activeStopIndex ? '#eee' : 'inherit'}`"
      >
        <span>
          <span class="stock-listing__amount">{{ stop.amount }}x in</span>
          {{ productTitle(stop.productId) }}
          <ExpiryDateBadge v-if="stop.expiryDate" :expiryDate="stop.expiryDate" class="ml-2" :minDaysToShow="30" />
        </span>

        <ShelfBadge :label="stop.shelfSpaceId" />
      </li>
    </ul>
    <div v-else class="text-right">
      <div
        class="alert text-center pt-5 pb-5"
        :class="stats.missing === 0 ? 'alert-success' : 'alert-secondary'"
        role="alert"
      >
        <p v-if="stats.missing === 0">All picked, nothing's missing!</p>
        <p v-else>
          There are no open stops but <span class="badge badge-warning">{{ stats.missing }} products</span> are missing.
        </p>
      </div>
    </div>
    <Layer title="Scan Wagon Box" v-if="scannedProductId" @close="scannedProductId = null">
      <div class="text-center m-5">
        <h5 class="mb-3">Scan Box for</h5>
        <div
          :style="
            `background-image: url(https://storage.googleapis.com/emma-static/products/${
              product(activeStop.productId).img
            }_1200.jpg)`
          "
          class="pick__active-stop__product-img"
          alt="..."
        />
        <button class="btn btn-sm btn-outline-secondary" @click="completeStop(null)">Continue with no Wagon Box</button>
      </div>
      <Scanner :expectations="['boxSpace']" :redirect="{ boxSpace: false }" @scan="handleBoxScan" />
    </Layer>
    <Layer title="Completed Stops" padding="2rem 1rem" v-if="showCompletedStops" @close="showCompletedStops = false">
      <ul class="list-group" v-if="stats.completed > 0">
        <li
          v-for="(pick, i) in order.pickedItems"
          :key="i"
          class="list-group-item d-flex justify-content-between align-items-center stock-listing__item"
        >
          <span>
            <span class="stock-listing__amount">{{ pick.amount }}x in</span>
            {{ productTitle(pick.productId) }}
          </span>
          <ShelfBadge :label="pick.shelfSpaceId" />
        </li>
      </ul>
      <p v-else class="text-center">There are no completed stops yet.</p>
    </Layer>
    <Scanner :expectations="['ean']" :redirect="{ ean: false }" @scan="handleProductScan" v-if="!scannedProductId" />
  </div>
</template>

<script>
import { fb, db, auth } from '@/shared/firebase'
import Layer from '@/shared/Layer'
import Scanner from '@/components/Scanner'
import ShelfBadge from '@/components/ShelfBadge'
import PickPlan from '@/helpers/PickPlan'
import ExpiryDateBadge from '@/components/ExpiryDateBadge'

export default {
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  components: { Layer, Scanner, ShelfBadge, ExpiryDateBadge },
  data() {
    return {
      activeStopIndex: 0,
      scannedProductId: null,
      showCompletedStops: false
    }
  },
  computed: {
    orderId() {
      return this.$route.params.orderId
    },
    pickPlan() {
      return PickPlan.calculate(this.order)
    },
    stats() {
      const stats = {
        open: this.pickPlan.open.length,
        completed: this.order.pickedItems ? this.order.pickedItems.length : 0,
        missing: Object.keys(this.pickPlan.missing).length
      }
      return stats
    },
    activeStop() {
      return this.pickPlan.open[this.activeStopIndex]
    }
  },
  methods: {
    handleProductScan(scan) {
      const productId = scan.data.id
      if (productId === this.activeStop.productId) {
        this.scannedProductId = productId
      }
    },
    handleBoxScan(scan) {
      this.completeStop(scan.q)
    },
    completeStop(boxSpaceId) {
      const { productId, amount, shelfSpaceId } = this.activeStop
      let shelf = _.find(this.$store.state.shelves, { id: shelfSpaceId.split('-')[0] }).spaces[
        shelfSpaceId.split('-')[1]
      ]
      shelf = _.cloneDeep(shelf)
      const itemIndex = _.findIndex(shelf.items, { productId })
      const expiryDate = shelf.items[itemIndex].expiryDate
      shelf.items[itemIndex].amount += -amount
      if (shelf.items[itemIndex].amount === 0) {
        shelf.items.splice(itemIndex, 1)
      }
      const batch = db.batch()

      const today = new Date().toISOString().substr(0, 10)
      batch.set(
        db.doc(`private/logs/warehouseActivities/${today}`),
        {
          [auth.currentUser.uid]: fb.firestore.FieldValue.arrayUnion({
            action: 'pick',
            timestamp: new Date(),
            orderId: this.orderId
          })
        },
        { merge: true }
      )
      const pickedItem = {
        productId,
        amount,
        shelfSpaceId
      }
      if (expiryDate) pickedItem.expiryDate = expiryDate
      if (boxSpaceId) pickedItem.boxSpaceId = boxSpaceId
      batch.update(db.doc(`orders/${this.orderId}`), {
        pickedItems: fb.firestore.FieldValue.arrayUnion(pickedItem)
      })
      batch.update(db.doc(`private/warehouse/shelves/${shelfSpaceId.split('-')[0]}`), {
        [`spaces.${shelfSpaceId.split('-')[1]}.items`]: shelf.items
      })
      batch.commit().then(() => {
        this.scannedProductId = 0
      })
    },
    product(productId) {
      return this.$store.state.products.find(p => p.id === productId)
    },
    productTitle(productId) {
      const product = this.product(productId)
      return `${product.brandName} ${product.label}`
    }
  }
}
</script>

<style lang="scss" scoped>
.pick {
  &__active-stop {
    display: flex;
    align-items: center;
    justify-content: space-around;
    margin: 2rem;
    &__shelf-badge {
      display: inline-block;
      font-size: 3rem;
    }
    &__product-img {
      width: 18rem;
      height: 18rem;
      display: inline-block;
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;
    }
    &__amount {
      font-size: 3rem;
      background: $color-alert-yellow;
      padding: 0.25rem 1rem;
      border-radius: 10px;
      position: absolute;
      bottom: 2rem;
      left: 0;
    }
  }
}
</style>
